/*
 * svnty-drawer.js - THE DRAWER's four behaviours (SVNTY, 2026-09-19, night build E).
 * Classic deferred asset. No deps, ASCII only, no console.
 *
 * Parents, read-only (the sibling store's theme assets):
 *   sib-accordion.js      -> 1. THE ACCORDION (the height tween, interrupt-safe, is-closing)
 *   sib-custom.js L105+   -> 2. THE POLICY SHEET (rise, then land the last row above the dock)
 *   header-drawer.liquid    -> 3. ONE SEARCH (a tap on the drawer's field opens THE SEARCH SHEET)
 *   sib-command-bar.js    -> 4. THE DOCK's bag count, mirrored from the header, never a second store
 *
 * WHY EVERYTHING GOES THROUGH ONE DOCUMENT-CAPTURE CLICK LISTENER
 * Trade's `MenuDrawer` (assets/global.js) binds, in its constructor, a listener to EVERY <summary> and
 * EVERY <button> inside <header-drawer>:
 *   - a summary runs `onSummaryClick`, which adds `submenu-open` to the closest `.has-submenu` and
 *     drill-slides Trade's panel - it would shatter an accordion;
 *   - a button runs `onCloseButtonClick` -> `closeSubmenu(closest('details'))`, and for a button that
 *     is not inside a nested <details> that closest IS the drawer's own details: the whole drawer would
 *     close on a tap of the policies chip.
 * Those listeners sit on the target itself, so they cannot be out-ordered by another listener on the
 * same element. A capture listener on an ANCESTOR always runs first, and `stopImmediatePropagation()`
 * there prevents the target's own listeners from ever being invoked, while leaving the DEFAULT action
 * intact (a <details> still toggles natively, a form still submits). That is the whole trick; nothing
 * in Trade is patched, forked or re-bound.
 *
 * WHAT IT MUST NEVER DO
 *   - hold a scroll lock. Trade's `overflow-hidden-<breakpoint>` body class is THE ONE HOLDER, and
 *     assets/svnty-header.js mirrors it onto <html>. This file only ever asks Trade to open or close
 *     its own surfaces, through their own controls, so the lock has exactly one owner at all times.
 *   - focus a field on open (TRAPS IOS-20: the keyboard would cover the sheet it opened).
 *   - measure the sticky header or write a length into it (TRAPS IOS-01/02/03).
 */
(function () {
  'use strict';
  if (window.__svntyDrawerInit) return;
  window.__svntyDrawerInit = true;

  var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  /* ======================================================================= *
   * 1. THE ACCORDION - ported from sib-accordion.js, scoped to details[data-svnty-acc]
   * ======================================================================= */
  var DURATION_OPEN = 380;
  var DURATION_CLOSE = 300;
  var EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
  var canAnimate = 'animate' in Element.prototype;
  var running = typeof WeakMap === 'function' ? new WeakMap() : null;

  function boxExtra(el) {
    var cs = window.getComputedStyle(el);
    return (
      (parseFloat(cs.borderTopWidth) || 0) +
      (parseFloat(cs.borderBottomWidth) || 0) +
      (parseFloat(cs.paddingTop) || 0) +
      (parseFloat(cs.paddingBottom) || 0)
    );
  }

  function cancelRun(details) {
    if (!running) return;
    var run = running.get(details);
    if (!run) return;
    if (run.height) run.height.cancel();
    if (run.panel) run.panel.cancel();
    running.delete(details);
  }

  function settle(details) {
    details.style.height = '';
    details.style.overflow = '';
    details.classList.remove('is-closing', 'is-animating');
    if (running) running.delete(details);
  }

  function openAcc(details, summary, panel) {
    var start = details.offsetHeight; /* the current visual height, mid-tween included */
    cancelRun(details);
    details.classList.remove('is-closing');
    details.classList.add('is-animating');
    details.style.overflow = 'hidden';
    details.style.height = '';
    details.open = true;
    var end = details.offsetHeight;
    var height = details.animate({ height: [start + 'px', end + 'px'] }, { duration: DURATION_OPEN, easing: EASE });
    var fade = panel
      ? panel.animate(
          { opacity: [0, 1], transform: ['translateY(-6px)', 'translateY(0)'] },
          { duration: DURATION_OPEN - 60, delay: 60, easing: EASE, fill: 'backwards' }
        )
      : null;
    if (running) running.set(details, { height: height, panel: fade, state: 'opening' });
    height.onfinish = function () {
      settle(details);
    };
  }

  function closeAcc(details, summary, panel) {
    var start = details.offsetHeight;
    cancelRun(details);
    details.classList.add('is-closing', 'is-animating');
    details.style.overflow = 'hidden';
    var end = summary.offsetHeight + boxExtra(details);
    var height = details.animate({ height: [start + 'px', end + 'px'] }, { duration: DURATION_CLOSE, easing: EASE });
    var fade = panel
      ? panel.animate(
          { opacity: [1, 0], transform: ['translateY(0)', 'translateY(-4px)'] },
          { duration: DURATION_CLOSE * 0.6, easing: 'ease-out', fill: 'forwards' }
        )
      : null;
    if (running) running.set(details, { height: height, panel: fade, state: 'closing' });
    height.onfinish = function () {
      if (fade) fade.cancel(); /* release the forwards fill before the panel hides */
      details.open = false; /* the [open] attribute stays true until the motion is done */
      settle(details);
    };
  }

  function handleAccordion(event, summary) {
    var details = summary.parentElement;
    if (!details || details.tagName !== 'DETAILS' || !details.hasAttribute('data-svnty-acc')) return;
    /* a link inside a summary keeps its own job */
    if (event.target.closest && event.target.closest('a')) return;
    if (!canAnimate || reduce.matches) return; /* the native toggle runs untouched */
    event.preventDefault();
    var panel = summary.nextElementSibling;
    var run = running ? running.get(details) : null;
    var isOpen = run ? run.state === 'opening' : details.open;
    if (isOpen) closeAcc(details, summary, panel);
    else openAcc(details, summary, panel);
  }

  /* ======================================================================= *
   * 2. THE POLICY SHEET
   * ======================================================================= */
  function scrollerOf(el) {
    var n = el.parentElement;
    while (n && n !== document.body) {
      var o = window.getComputedStyle(n).overflowY;
      if ((o === 'auto' || o === 'scroll') && n.scrollHeight > n.clientHeight) return n;
      n = n.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  }

  function dockHeight() {
    var dock = document.querySelector('.menu-drawer .svnty-drawer-dock');
    return dock ? dock.getBoundingClientRect().height + 16 : 96;
  }

  function handlePolicies(btn) {
    var sheet = document.getElementById(btn.getAttribute('aria-controls'));
    if (!sheet) return;
    var opening = btn.getAttribute('aria-expanded') !== 'true';
    var sc = scrollerOf(sheet);
    btn.setAttribute('aria-expanded', opening ? 'true' : 'false');

    if (!opening) {
      sheet.classList.remove('is-open');
      var back = parseInt(btn.getAttribute('data-svnty-return-top') || '0', 10) || 0;
      sc.scrollTo({ top: back, behavior: reduce.matches ? 'auto' : 'smooth' });
      var hid = false;
      var hide = function () {
        if (hid) return;
        hid = true;
        sheet.removeEventListener('transitionend', hide);
        if (btn.getAttribute('aria-expanded') !== 'true') sheet.hidden = true;
      };
      sheet.addEventListener('transitionend', hide);
      window.setTimeout(hide, 360);
      return;
    }

    btn.setAttribute('data-svnty-return-top', String(sc.scrollTop));
    sheet.hidden = false;
    window.requestAnimationFrame(function () {
      sheet.classList.add('is-open');
      /* THE PULL DOWN (the sibling): follow the sheet as it grows, land with the last row above the dock */
      var landed = false;
      var land = function () {
        if (landed) return;
        landed = true;
        sheet.removeEventListener('transitionend', land);
        if (btn.getAttribute('aria-expanded') !== 'true') return;
        var r = sheet.getBoundingClientRect();
        var sr = sc.getBoundingClientRect();
        var target = sc.scrollTop + (r.bottom - sr.bottom) + dockHeight();
        sc.scrollTo({ top: Math.max(0, target), behavior: reduce.matches ? 'auto' : 'smooth' });
      };
      sheet.addEventListener('transitionend', land);
      window.setTimeout(land, 340);
    });
  }

  /* ======================================================================= *
   * 3. ONE SEARCH - the drawer's field opens THE SEARCH SHEET
   * ======================================================================= */
  var swallowUntil = 0;
  var cameFromDrawer = false;
  var watched = null;

  function drawerDetails() {
    return document.getElementById('Details-menu-drawer-container');
  }

  function searchModal() {
    var all = document.querySelectorAll('details-modal.header__search');
    for (var i = 0; i < all.length; i++) {
      if (all[i].offsetParent !== null) return all[i];
    }
    return all.length ? all[0] : null;
  }

  /* the way back (the sibling's is-from-drawer): when a sheet opened from the drawer closes, the drawer
     comes back - but the field is NOT re-focused here, because focusing a field raises the keyboard
     over the sheet it opened (IOS-20). */
  function watchSheet(details) {
    if (!details || watched === details || typeof MutationObserver !== 'function') return;
    watched = details;
    new MutationObserver(function () {
      if (details.hasAttribute('open') || !cameFromDrawer) return;
      cameFromDrawer = false;
      window.setTimeout(function () {
        var d = drawerDetails();
        var summary = d && d.querySelector('summary');
        if (d && summary && !d.hasAttribute('open')) summary.click();
      }, 80);
    }).observe(details, { attributes: true, attributeFilter: ['open'] });
  }

  function openSheetFromDrawer(input) {
    var modal = searchModal();
    if (!modal) return false;
    var details = modal.querySelector('details');
    var summary = modal.querySelector('summary');
    if (!details || !summary) return false;

    try {
      input.blur();
    } catch (err) {
      /* nothing */
    }
    swallowUntil = Date.now() + 700;
    cameFromDrawer = true;
    watchSheet(details);

    /* close the drawer through Trade's OWN control, so Trade releases its own lock and its own focus
       trap - never by hand, never a second lock holder */
    var d = drawerDetails();
    var burger = d && d.querySelector('summary');
    if (d && burger && d.hasAttribute('open')) burger.click();

    window.requestAnimationFrame(function () {
      if (!details.hasAttribute('open')) summary.click();
    });
    return true;
  }

  document.addEventListener(
    'pointerdown',
    function (event) {
      if (!event.target || !event.target.closest) return;
      var input = event.target.closest('.svnty-drawer-search__input');
      if (!input) return;
      if (openSheetFromDrawer(input)) event.preventDefault();
    },
    true
  );

  /* ======================================================================= *
   * THE ONE CAPTURE LISTENER
   * ======================================================================= */
  document.addEventListener(
    'click',
    function (event) {
      var t = event.target;
      if (!t || !t.closest) return;

      /* the stray click that follows the pointerdown handoff: it must never reach details-modal's own
         body listener, which reads a tap outside the sheet as "close" (the sheet had just opened) */
      if (swallowUntil > Date.now() && event.isTrusted) {   /* the REAL click of the handoff, whatever it lands on (the sheet slides under the pointer); never the synthetic clicks the handoff itself sends to Trade's controls */
        swallowUntil = 0;
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      var summary = t.closest('summary[data-svnty-acc-sum]');
      if (summary) {
        event.stopImmediatePropagation(); /* Trade's onSummaryClick must not run on an accordion */
        handleAccordion(event, summary);
        return;
      }

      var policies = t.closest('[data-svnty-policies-toggle]');
      if (policies) {
        event.preventDefault();
        event.stopImmediatePropagation(); /* Trade's onCloseButtonClick would close the whole drawer */
        handlePolicies(policies);
        return;
      }

      var submit = t.closest('.svnty-drawer-search__submit');
      if (submit) {
        event.stopImmediatePropagation(); /* same reason; the form's own submit is the default action */
        return;
      }
    },
    true
  );

  /* ======================================================================= *
   * 4. THE DOCK's BAG COUNT - mirrored from the header, the single source of truth
   * ======================================================================= */
  function headerBagCount() {
    var el = document.querySelector('#cart-icon-bubble [data-svnty-cart-count]');
    if (!el) return 0;
    if (el.hasAttribute('hidden')) return 0;
    var n = parseInt(el.textContent, 10);
    return isNaN(n) ? 0 : n;
  }

  function paint(el, n) {
    var label = String(Math.min(Math.max(0, n | 0), 99)); /* the bubble is a disc: two digits, never more */
    if (el.textContent !== label) el.textContent = label;
    var hide = n <= 0;
    if (el.hasAttribute('hidden') !== hide) el.toggleAttribute('hidden', hide);
  }

  var queued = false;
  function syncCounts() {
    queued = false;
    var bubbles = document.querySelectorAll('[data-svnty-udock-count="cart"]');
    if (!bubbles.length) return;
    var n = headerBagCount();
    for (var i = 0; i < bubbles.length; i++) paint(bubbles[i], n);
  }
  function schedule() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(syncCounts);
  }

  syncCounts();
  document.addEventListener('DOMContentLoaded', schedule);
  window.addEventListener('load', schedule);
  window.addEventListener('pageshow', schedule);
  window.addEventListener('storage', schedule);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) schedule();
  });
  /* Trade re-renders the whole cart-icon-bubble SECTION after every add, so the header's own number is
     the only thing worth watching (the sibling's law: six ways to refresh, no path leaves a bar stale) */
  if (typeof MutationObserver === 'function') {
    var host = document.querySelector('.section-header') || document.querySelector('.header') || document.body;
    if (host) {
      new MutationObserver(schedule).observe(host, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['hidden', 'class'],
      });
    }
  }
  /* and the theme's own bus, when it is on the page */
  try {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
    }
  } catch (err) {
    /* pubsub.js is not on this page */
  }
  document.addEventListener('toggle', schedule, true); /* a drawer or a sheet just opened */

  /* THE TILES READY (P17-27) retired with the tiles (P34-9) */
})();
